import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { createChart, CrosshairMode } from "lightweight-charts";
import { optionChartAPI, strategyAPI, backtestAPI, dataAnalysisAPI } from "../services/api";

// Crypto chart page for Delta (BTC/ETH). Three sub-views:
//   • Perp   — the underlying perp candles + inside-bar backtest overlay (deep-linkable
//              per leg from the backtest transaction history).
//   • Strike — ONE option contract's premium history (pick expiry + strike + CE/PE).
//   • VIX    — day-wise ATM implied-volatility series (Black-Scholes), a VIX-like gauge.

const C = { card: "#fff", border: "#e8edf3", text: "#0f172a", sub: "#64748b", blue: "#2563eb", green: "#10b981", red: "#ef4444", bg: "#f8fafc", amber: "#f59e0b" };
const IB = "#7c3aed";
const card = (extra = {}) => ({ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, ...extra });
const sel = (extra = {}) => ({ padding: "9px 10px", border: "1px solid #e8edf3", borderRadius: 8, fontSize: 14, background: "#fff", color: "#0f172a", cursor: "pointer", ...extra });
const UNDERLYINGS = ["BTCUSD", "ETHUSD"];
const OPT_UNDERLYINGS = ["BTC", "ETH"];
const TFS = [
  { id: "1minute", label: "1m" }, { id: "5minute", label: "5m" }, { id: "15minute", label: "15m" },
  { id: "30minute", label: "30m" }, { id: "60minute", label: "1h" }, { id: "4hour", label: "4h" },
];
// Strategy insideBar.timeframe token → chart interval id (4h shares one id).
const STRAT_TF_TO_INTERVAL = { "1min": "1minute", "5min": "5minute", "15min": "15minute", "30min": "30minute", "60min": "60minute", "1hour": "60minute", "2hour": "4hour", "4hour": "4hour", "1day": "4hour" };

// Perp/Strike endpoints label candle times in IST (endpoint adds +5:30). Feed that
// wall-clock AS IF UTC so lightweight-charts shows it verbatim; subtract `offMin` (330
// for a UTC view) to shift the axis to real UTC. offMin 0 = show the times as-labelled
// (IST for perp/strike; the VIX candle series is already UTC-labelled → also 0).
const TZ_OFF = { UTC: 330, IST: 0 };
const toTime = (c, offMin = 0) => Math.floor(Date.parse(`${c.date}T${c.time}:00Z`) / 1000) - offMin * 60;
const isoDate = (d) => d.toISOString().slice(0, 10);
const shiftDays = (dateStr, n) => { const d = new Date((dateStr || isoDate(new Date())) + "T00:00:00Z"); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };

// Inside bar = fully engulfed by the previous candle (never marks the first bar).
function detectInside(cs) {
  const ins = new Array(cs.length).fill(false);
  for (let i = 1; i < cs.length; i++) { const c = cs[i], p = cs[i - 1]; ins[i] = c.h < p.h && c.l > p.l; }
  return ins;
}

const chip = (active) => ({ padding: "8px 16px", borderRadius: 8, border: `1px solid ${active ? C.blue : C.border}`, background: active ? C.blue : "#fff", color: active ? "#fff" : C.text, fontWeight: 600, cursor: "pointer" });

// ─────────────────────────────────────────────────────────────────────────────
// Page shell: hero + Perp / Strike / VIX sub-tabs. A per-leg deep link (strategyId
// / date in the URL) always lands on Perp (that's where the inside-bar overlay is).
// Parse a crypto option symbol (BTC24D03100000CE) → { underlying, expiry, strike, optType }.
// Mirrors the backend buildSymbol: <UND><YY><MOCODE><DD><STRIKE><CE|PE>, months O/N/D = 10/11/12.
const CRYPTO_MO = { O: 10, N: 11, D: 12 };
function parseCryptoSym(raw) {
  const s = String(raw || "").toUpperCase();
  const und = OPT_UNDERLYINGS.find(u => s.startsWith(u));
  if (!und) return null;
  const m = /^(\d{2})([1-9OND])(\d{2})(\d+)(CE|PE)$/.exec(s.slice(und.length));
  if (!m) return null;
  const mo = CRYPTO_MO[m[2]] || Number(m[2]);
  return { underlying: und, expiry: `20${m[1]}-${String(mo).padStart(2, "0")}-${m[3]}`, strike: Number(m[4]), optType: m[5] };
}

export default function CryptoChart() {
  const [params] = useSearchParams();
  // Strike deep-link (from a crypto option leg's "View chart"): open the Strike sub-tab
  // pre-loaded with that exact contract's premium chart, not the perp.
  const parsedSym = params.get("view") === "strike" ? parseCryptoSym(params.get("symbol")) : null;
  const strikeDeep = parsedSym ? {
    ...parsedSym,
    interval: STRAT_TF_TO_INTERVAL[params.get("tf")] || "15minute",
  } : null;
  const deepLink = !!(params.get("strategyId") || params.get("date"));
  const [mode, setMode] = useState(strikeDeep ? "strike" : "perp");
  // Chart time axis. Delta is UTC-native (candles/expiries/settlement all UTC) and the
  // backtest gates entries on UTC, so default to UTC; user can flip to IST.
  const [tz, setTz] = useState("UTC");

  const tab = (id, label) => (
    <button key={id} onClick={() => setMode(id)} style={{ padding: "9px 20px", borderRadius: 9, border: `1px solid ${mode === id ? C.blue : C.border}`, background: mode === id ? C.blue : "#fff", color: mode === id ? "#fff" : C.text, fontWeight: 700, cursor: "pointer", fontSize: 14 }}>{label}</button>
  );

  return (
    <div>
      <div className="page-hero" style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
        <div className="hero-icon" style={{ width: 46, height: 46, borderRadius: 13, background: "linear-gradient(135deg,#2563eb,#4f46e5)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 23 }}>📉</div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: "-0.3px", color: C.text }}>Chart</h1>
        <div style={{ display: "flex", gap: 8, marginLeft: "auto", flexWrap: "wrap", alignItems: "center" }}>
          {mode !== "vix" && (
            <div style={{ display: "flex", marginRight: 4 }} title="Chart time zone">
              {["UTC", "IST"].map(z => (
                <button key={z} onClick={() => setTz(z)}
                  style={{ padding: "9px 13px", fontSize: 13, fontWeight: 700, cursor: "pointer", border: `1px solid ${tz === z ? C.blue : C.border}`, background: tz === z ? C.blue : "#fff", color: tz === z ? "#fff" : C.text, borderRadius: z === "UTC" ? "9px 0 0 9px" : "0 9px 9px 0", marginLeft: z === "IST" ? -1 : 0 }}>{z}</button>
              ))}
            </div>
          )}
          {tab("perp", "Perp")}{tab("strike", "Strike")}{tab("vix", "VIX")}
        </div>
      </div>

      {mode === "perp" && <PerpChart deepLink={deepLink} tz={tz} />}
      {mode === "strike" && <StrikeChart deep={strikeDeep} tz={tz} />}
      {mode === "vix" && <VixChart />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PERP — underlying perp candles + inside-bar backtest overlay (deep-linkable).
function PerpChart({ tz = "UTC" } = {}) {
  const off = TZ_OFF[tz] || 0; // minutes to shift the IST-labelled candle times (UTC → 330)
  const [params] = useSearchParams();
  const pStrategyId = params.get("strategyId") || "";
  const pDate = params.get("date") || "";
  const pUnd = (params.get("underlying") || "").toUpperCase();
  const pTf = params.get("tf") || "";

  const [underlying, setUnderlying] = useState(UNDERLYINGS.includes(pUnd) ? pUnd : (pUnd === "BTC" ? "BTCUSD" : pUnd === "ETH" ? "ETHUSD" : "BTCUSD"));
  const [interval, setIntervalTf] = useState(pTf && STRAT_TF_TO_INTERVAL[pTf] ? STRAT_TF_TO_INTERVAL[pTf] : (TFS.some(t => t.id === pTf) ? pTf : "15minute"));
  const [from, setFrom] = useState(pDate ? shiftDays(pDate, -3) : shiftDays(isoDate(new Date()), -7));
  const [to, setTo] = useState(pDate ? shiftDays(pDate, 2) : isoDate(new Date()));
  const [day, setDay] = useState(pDate || "all");       // zoom + draw SL/target lines for one IST day
  const [stratId, setStratId] = useState(pStrategyId);  // overlay strategy

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [ibStrats, setIbStrats] = useState([]);
  const [ov, setOv] = useState(null);
  const [ovLoading, setOvLoading] = useState(false);
  const [ovErr, setOvErr] = useState(null);

  // Chart refs
  const elRef = useRef(null), chartRef = useRef(null), mainRef = useRef(null), seriesRef = useRef([]);
  const [legend, setLegend] = useState(null);

  // Load the user's inside-bar strategies (for the overlay selector + tint config).
  useEffect(() => { let alive = true; strategyAPI.getAll().then(r => { if (alive) setIbStrats((r.data || []).filter(s => s?.insideBar?.enabled)); }).catch(() => {}); return () => { alive = false; }; }, []);

  const loadCandles = useCallback(async (und, tf, f, t) => {
    setLoading(true); setMsg(null);
    try {
      const res = await optionChartAPI.cryptoIndex(und, tf, f, t);
      setData(res.data);
      if (!res.data.candles?.length) setMsg({ type: "warn", text: "No data in this range." });
    } catch (e) { setData(null); setMsg({ type: "err", text: e.response?.data?.message || e.message }); }
    finally { setLoading(false); }
  }, []);

  const runOverlay = useCallback(async (sid, f, t) => {
    if (!sid) return;
    setOvLoading(true); setOvErr(null);
    try { const r = await backtestAPI.overlay(sid, f, t); setOv(r.data); }
    catch (e) { setOv(null); setOvErr(e.response?.data?.message || e.message); }
    finally { setOvLoading(false); }
  }, []);

  // Initial / deep-link load (runs once on mount and whenever the URL params change).
  useEffect(() => {
    loadCandles(underlying, interval, from, to);
    if (stratId) runOverlay(stratId, from, to);
  }, [pStrategyId, pDate, pUnd, pTf]); // eslint-disable-line

  const cs = useMemo(() => (data?.candles || []).map(c => ({ ...c, _t: toTime(c, off) })), [data, off]);
  const days = useMemo(() => [...new Set(cs.map(c => c.date))], [cs]);
  useEffect(() => { if (day !== "all" && days.length && !days.includes(day)) setDay("all"); }, [days, day]);

  // Inside-bar tint (per the selected strategy's config; detected on the displayed candles
  // since they're already at the strategy's timeframe via the deep link).
  const ibFlags = useMemo(() => (stratId && cs.length) ? detectInside(cs) : [], [stratId, cs]);

  const ovTrades = useMemo(() => {
    if (!ov?.trades) return [];
    return day === "all" ? ov.trades : ov.trades.filter(t => t.date === day);
  }, [ov, day]);
  const tByKey = useMemo(() => { const m = new Map(); cs.forEach(c => m.set(c.date + " " + c.time, c._t)); return m; }, [cs]);
  const tAt = (d, hhmm) => {
    if (!d || !hhmm) return null;
    const hit = tByKey.get(d + " " + hhmm); if (hit != null) return hit;
    const want = Date.parse(`${d}T${hhmm}:00Z`) / 1000 - off * 60; // same tz basis as c._t
    // Snap to the candle that CONTAINS this time (largest start ≤ want), NOT the next one —
    // so an intra-candle entry/exit on a higher TF (e.g. a 04:49 breakout inside the 01:30
    // 4h bar) sits on the bar it actually happened in. Fall back to the day's first bar.
    let best = null;
    for (const c of cs) {
      if (c.date !== d) continue;
      if (c._t <= want) best = c._t; else break;
    }
    if (best == null) { const f = cs.find(c => c.date === d); best = f ? f._t : null; }
    return best;
  };

  // Create the chart once.
  useEffect(() => {
    const el = elRef.current; if (!el) return;
    const chart = createChart(el, {
      width: el.clientWidth, height: 520,
      layout: { background: { color: "#fff" }, textColor: "#334155", fontSize: 12 },
      grid: { vertLines: { color: "#eef2f7" }, horzLines: { color: "#eef2f7" } },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: C.border, scaleMargins: { top: 0.08, bottom: 0.26 } },
      timeScale: { borderColor: C.border, timeVisible: true, secondsVisible: false, rightOffset: 6 },
    });
    chartRef.current = chart;
    chart.subscribeCrosshairMove((p) => { if (!p.time || !p.seriesData || !mainRef.current) { setLegend(null); return; } setLegend(p.seriesData.get(mainRef.current) || null); });
    const ro = new ResizeObserver(() => chart.applyOptions({ width: el.clientWidth }));
    ro.observe(el);
    return () => { ro.disconnect(); chart.remove(); chartRef.current = null; };
  }, []);

  // (Re)build series on data / overlay / day change.
  useEffect(() => {
    const chart = chartRef.current; if (!chart || !cs.length) return;
    seriesRef.current.forEach(s => chart.removeSeries(s)); seriesRef.current = []; mainRef.current = null;
    const add = (s) => { seriesRef.current.push(s); return s; };
    const up = C.green, dn = C.red;
    const paint = (arr) => arr.map((d, i) => (ibFlags[i] && (day === "all" || cs[i].date === day)) ? { ...d, color: IB, borderColor: IB, wickColor: IB } : d);
    const main = add(chart.addCandlestickSeries({ upColor: up, downColor: dn, borderUpColor: up, borderDownColor: dn, wickUpColor: up, wickDownColor: dn }));
    main.setData(paint(cs.map(c => ({ time: c._t, open: c.o, high: c.h, low: c.l, close: c.c }))));
    mainRef.current = main;

    const markers = [];
    cs.forEach((c, i) => { if (ibFlags[i] && (day === "all" || c.date === day)) markers.push({ time: c._t, position: "aboveBar", color: IB, shape: "circle", text: "IB" }); });
    ovTrades.forEach((t) => {
      const leg0 = t.legs?.[0]; if (!leg0) return;
      const dir = t.ib?.dir;
      const et = tAt(t.date, leg0.entryTime);
      if (et != null) markers.push({ time: et, position: dir === "PE" ? "aboveBar" : "belowBar", color: dir === "PE" ? C.red : C.green, shape: dir === "PE" ? "arrowDown" : "arrowUp", text: "Entry" });
      let xt = null, xleg = leg0;
      t.legs.forEach((l) => { const z = tAt(t.date, l.exitTime); if (z != null && (xt == null || z > xt)) { xt = z; xleg = l; } });
      if (xt != null) markers.push({ time: xt, position: "aboveBar", color: t.win ? C.green : C.red, shape: "square", text: `${xleg.exitType || "EXIT"} $${Math.round(t.pnl)}` });
    });
    if (markers.length) { markers.sort((a, b) => a.time - b.time); main.setMarkers(markers); }

    // SL / target / breakout spot lines — only when a single day is in view (else clutter).
    if (ov && day !== "all") {
      ovTrades.filter(t => t.ib).forEach((t) => {
        const line = (price, color, title, style = 0) => { if (price != null) main.createPriceLine({ price, color, lineWidth: 1, lineStyle: style, axisLabelVisible: true, title }); };
        line(t.ib.breakoutSpot, C.blue, "Entry", 2);
        line(t.ib.slSpot, C.red, "SL");
        line(t.ib.targetSpot, C.green, "Target");
        if (t.ib.babyHigh != null) line(t.ib.babyHigh, IB, "IB H", 1);
        if (t.ib.babyLow != null) line(t.ib.babyLow, IB, "IB L", 1);
      });
    }

    const vol = add(chart.addHistogramSeries({ priceScaleId: "vol", priceFormat: { type: "volume" }, color: "#cbd5e1" }));
    vol.priceScale().applyOptions({ scaleMargins: { top: 0.84, bottom: 0 } });
    vol.setData(cs.map(c => ({ time: c._t, value: c.volume || 0, color: c.c >= c.o ? "rgba(16,185,129,.5)" : "rgba(239,68,68,.5)" })));

    // Zoom to the focused day (deep link) else the last day.
    const ts = chart.timeScale();
    if (day !== "all") { let f = -1, t = -1; cs.forEach((c, i) => { if (c.date === day) { if (f < 0) f = i; t = i; } }); if (f >= 0) ts.setVisibleLogicalRange({ from: f - 2, to: t + 3 }); }
    else { const last = cs[cs.length - 1].date; let s = cs.length - 1; while (s > 0 && cs[s - 1].date === last) s--; ts.setVisibleLogicalRange({ from: s - 1, to: cs.length + 3 }); }
  }, [cs, ibFlags, ov, ovTrades, day]);

  const reload = () => { loadCandles(underlying, interval, from, to); if (stratId) runOverlay(stratId, from, to); };

  return (
    <>
      <div style={card({ padding: 16, marginBottom: 16 })}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <label style={{ display: "block", fontSize: 12, color: C.sub, marginBottom: 6 }}>Underlying</label>
            <select value={underlying} onChange={e => setUnderlying(e.target.value)} style={sel()}>{UNDERLYINGS.map(u => <option key={u} value={u}>{u}</option>)}</select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: C.sub, marginBottom: 6 }}>From</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={sel()} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: C.sub, marginBottom: 6 }}>To</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} style={sel()} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: C.sub, marginBottom: 6 }}>Strategy overlay (optional)</label>
            <select value={stratId} onChange={e => { setStratId(e.target.value); if (e.target.value) runOverlay(e.target.value, from, to); else setOv(null); }} style={sel({ minWidth: 220 })}>
              <option value="">Off — just candles</option>
              {ibStrats.map(s => <option key={s._id} value={s._id}>{(s.name || "Strategy")} · {s.insideBar?.timeframe || "5min"}</option>)}
            </select>
          </div>
          <button onClick={reload} disabled={loading} style={{ ...chip(false), background: loading ? "#93c5fd" : C.blue, color: "#fff", border: "none" }}>{loading ? "Loading…" : "Load"}</button>
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap", alignItems: "center" }}>
          {TFS.map(t => <button key={t.id} onClick={() => { setIntervalTf(t.id); loadCandles(underlying, t.id, from, to); }} style={chip(interval === t.id)}>{t.label}</button>)}
          <span style={{ width: 1, height: 22, background: C.border }} />
          <select value={day} onChange={e => setDay(e.target.value)} style={sel({ padding: "6px 8px", fontSize: 13 })}>
            <option value="all">All days</option>
            {days.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          {ovLoading && <span style={{ fontSize: 12, color: C.sub }}>Running P&L…</span>}
          {ov && <span style={{ fontSize: 13, fontWeight: 700, color: ov.summary.pnl >= 0 ? C.green : C.red }}>Total ${Math.round(ov.summary.pnl).toLocaleString()} · {ov.summary.trades} trades · {ov.summary.winRate}% win</span>}
          {ovErr && <span style={{ fontSize: 12, color: C.red }}>{ovErr}</span>}
          <button onClick={() => chartRef.current?.timeScale().fitContent()} style={{ ...chip(false), marginLeft: "auto" }}>Fit</button>
        </div>
      </div>

      {msg && <div style={{ marginBottom: 14, padding: "12px 16px", borderRadius: 10, fontSize: 14, background: msg.type === "err" ? "#fee2e2" : "#fef9c3", color: msg.type === "err" ? "#991b1b" : "#854d0e" }}>{msg.text}</div>}

      <div style={card({ padding: 16 })}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, fontSize: 13, color: C.text }}>
          <span style={{ fontWeight: 700, fontFamily: "monospace" }}>{underlying} <span style={{ color: C.sub, fontWeight: 500 }}>Perp · {data?.meta?.count || 0} candles</span></span>
          <span style={{ fontSize: 11, color: C.sub }}>times {tz} · scroll = zoom · drag = pan · SL/Target lines show on a single day · $ P&L = real backtest</span>
        </div>
        <div style={{ position: "relative" }}>
          {legend && legend.open != null && (
            <div style={{ position: "absolute", top: 8, left: 10, zIndex: 3, display: "flex", gap: 12, fontSize: 12, background: "rgba(255,255,255,.9)", padding: "4px 10px", borderRadius: 6, border: `1px solid ${C.border}` }}>
              <span>O <b>{legend.open.toFixed(1)}</b></span>
              <span style={{ color: C.green }}>H <b>{legend.high.toFixed(1)}</b></span>
              <span style={{ color: C.red }}>L <b>{legend.low.toFixed(1)}</b></span>
              <span>C <b>{legend.close.toFixed(1)}</b></span>
            </div>
          )}
          <div ref={elRef} style={{ width: "100%", height: 520, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }} />
          {!cs.length && !loading && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: C.sub, fontSize: 14 }}>Pick a range and Load.</div>}
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STRIKE — one Delta option contract's premium history (expiry → strike → CE/PE).
function StrikeChart({ deep, tz = "UTC" } = {}) {
  const off = TZ_OFF[tz] || 0; // shift IST-labelled candle times to the chosen axis (UTC → 330)
  const [underlying, setUnderlying] = useState(deep?.underlying || "BTC");
  const [expiries, setExpiries] = useState([]);
  const [expiry, setExpiry] = useState(deep?.expiry || "");
  const [strikes, setStrikes] = useState([]);      // [{ strike, ce, pe }]
  const [strike, setStrike] = useState(deep?.strike != null ? String(deep.strike) : "");
  const [optType, setOptType] = useState(deep?.optType || "CE");
  const [interval, setIntervalTf] = useState(deep?.interval || "15minute");
  const [from, setFrom] = useState(shiftDays(isoDate(new Date()), -30));
  const [to, setTo] = useState(shiftDays(isoDate(new Date()), 1));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [legend, setLegend] = useState(null);

  const elRef = useRef(null), chartRef = useRef(null), mainRef = useRef(null), seriesRef = useRef([]);

  // Load expiries for the underlying; default to the newest that has data.
  useEffect(() => {
    let alive = true;
    optionChartAPI.cryptoExpiries(underlying).then(r => {
      if (!alive) return;
      const list = r.data.expiries || [];
      setExpiries(list);
      setExpiry(prev => (list.includes(prev) ? prev : (list[0] || "")));
    }).catch(() => { if (alive) { setExpiries([]); setExpiry(""); } });
    return () => { alive = false; };
  }, [underlying]);

  // Load strikes for the chosen expiry; default the strike to the middle of the ladder.
  // Also auto-fit the date range to the contract's life (expiry − 30d … expiry + 1d).
  useEffect(() => {
    if (!expiry) { setStrikes([]); setStrike(""); return; }
    let alive = true;
    setFrom(shiftDays(expiry, -30)); setTo(shiftDays(expiry, 1));
    optionChartAPI.cryptoStrikes(underlying, expiry).then(r => {
      if (!alive) return;
      const list = (r.data.strikes || []).filter(s => (optType === "CE" ? s.ce : s.pe) || (optType === "CE" ? s.pe : s.ce));
      setStrikes(list);
      setStrike(prev => {
        if (list.some(s => String(s.strike) === String(prev))) return prev;
        return list.length ? String(list[Math.floor(list.length / 2)].strike) : "";
      });
    }).catch(() => { if (alive) { setStrikes([]); setStrike(""); } });
    return () => { alive = false; };
  }, [underlying, expiry]); // eslint-disable-line

  // ov lets a strike-list click / CE-PE toggle load immediately without waiting for
  // the setState to flush (state closure would otherwise be one render behind).
  const load = useCallback(async (tf, ov = {}) => {
    const st = ov.strike != null ? String(ov.strike) : strike;
    const ot = ov.optType || optType;
    if (!expiry || !st) { setMsg({ type: "warn", text: "Pick an expiry and strike." }); return; }
    setLoading(true); setMsg(null);
    try {
      const res = await optionChartAPI.cryptoOption(underlying, { expiry, strike: st, optType: ot, interval: tf || interval, from, to });
      setData(res.data);
      if (!res.data.candles?.length) setMsg({ type: "warn", text: "No candles in this range." });
    } catch (e) { setData(null); setMsg({ type: "err", text: e.response?.data?.message || e.message }); }
    finally { setLoading(false); }
  }, [underlying, expiry, strike, optType, interval, from, to]);

  const pickStrike = (st) => { setStrike(String(st)); load(undefined, { strike: st }); };

  // Deep-link auto-load (per-leg "View chart"): once the deep expiry's strikes are in and
  // the deep strike is the selected one — the strikes effect keeps it because a backtest
  // leg's strike is always a LISTED strike — fire the chart load exactly once.
  const autoRef = useRef(false);
  useEffect(() => {
    if (autoRef.current || !deep) return;
    if (!expiry || !strikes.length || String(strike) !== String(deep.strike)) return;
    autoRef.current = true;
    load();
  }, [strikes, strike, expiry, deep, load]);

  const cs = useMemo(() => (data?.candles || []).map(c => ({ ...c, _t: toTime(c, off) })), [data, off]);

  useEffect(() => {
    const el = elRef.current; if (!el) return;
    const chart = createChart(el, {
      width: el.clientWidth, height: 520,
      layout: { background: { color: "#fff" }, textColor: "#334155", fontSize: 12 },
      grid: { vertLines: { color: "#eef2f7" }, horzLines: { color: "#eef2f7" } },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: C.border, scaleMargins: { top: 0.08, bottom: 0.26 } },
      timeScale: { borderColor: C.border, timeVisible: true, secondsVisible: false, rightOffset: 6 },
    });
    chartRef.current = chart;
    chart.subscribeCrosshairMove((p) => { if (!p.time || !p.seriesData || !mainRef.current) { setLegend(null); return; } setLegend(p.seriesData.get(mainRef.current) || null); });
    const ro = new ResizeObserver(() => chart.applyOptions({ width: el.clientWidth }));
    ro.observe(el);
    return () => { ro.disconnect(); chart.remove(); chartRef.current = null; };
  }, []);

  useEffect(() => {
    const chart = chartRef.current; if (!chart) return;
    seriesRef.current.forEach(s => chart.removeSeries(s)); seriesRef.current = []; mainRef.current = null;
    if (!cs.length) return;
    const add = (s) => { seriesRef.current.push(s); return s; };
    const up = C.green, dn = C.red;
    const main = add(chart.addCandlestickSeries({ upColor: up, downColor: dn, borderUpColor: up, borderDownColor: dn, wickUpColor: up, wickDownColor: dn }));
    main.setData(cs.map(c => ({ time: c._t, open: c.o, high: c.h, low: c.l, close: c.c })));
    mainRef.current = main;
    const vol = add(chart.addHistogramSeries({ priceScaleId: "vol", priceFormat: { type: "volume" }, color: "#cbd5e1" }));
    vol.priceScale().applyOptions({ scaleMargins: { top: 0.84, bottom: 0 } });
    vol.setData(cs.map(c => ({ time: c._t, value: c.volume || 0, color: c.c >= c.o ? "rgba(16,185,129,.5)" : "rgba(239,68,68,.5)" })));
    chart.timeScale().fitContent();
  }, [cs]);

  return (
    <>
      <div style={card({ padding: 16, marginBottom: 16 })}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <label style={{ display: "block", fontSize: 12, color: C.sub, marginBottom: 6 }}>Underlying</label>
            <select value={underlying} onChange={e => setUnderlying(e.target.value)} style={sel()}>{OPT_UNDERLYINGS.map(u => <option key={u} value={u}>{u}</option>)}</select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: C.sub, marginBottom: 6 }}>Expiry</label>
            <select value={expiry} onChange={e => setExpiry(e.target.value)} style={sel({ minWidth: 130 })}>
              {!expiries.length && <option value="">— none —</option>}
              {expiries.map(x => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: C.sub, marginBottom: 6 }}>Type</label>
            <div style={{ display: "flex", gap: 0 }}>
              {["CE", "PE"].map(o => <button key={o} onClick={() => { setOptType(o); if (strike) load(undefined, { optType: o }); }} style={{ ...chip(optType === o), borderRadius: o === "CE" ? "8px 0 0 8px" : "0 8px 8px 0", background: optType === o ? (o === "CE" ? C.green : C.red) : "#fff", borderColor: optType === o ? (o === "CE" ? C.green : C.red) : C.border, color: optType === o ? "#fff" : C.text }}>{o}</button>)}
            </div>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: C.sub, marginBottom: 6 }}>From</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={sel()} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: C.sub, marginBottom: 6 }}>To</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} style={sel()} />
          </div>
          <button onClick={() => load()} disabled={loading || !strike} style={{ ...chip(false), background: loading ? "#93c5fd" : C.blue, color: "#fff", border: "none" }}>{loading ? "Loading…" : "Load"}</button>
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap", alignItems: "center" }}>
          {TFS.map(t => <button key={t.id} onClick={() => { setIntervalTf(t.id); load(t.id); }} style={chip(interval === t.id)}>{t.label}</button>)}
          <button onClick={() => chartRef.current?.timeScale().fitContent()} style={{ ...chip(false), marginLeft: "auto" }}>Fit</button>
        </div>
      </div>

      {msg && <div style={{ marginBottom: 14, padding: "12px 16px", borderRadius: 10, fontSize: 14, background: msg.type === "err" ? "#fee2e2" : "#fef9c3", color: msg.type === "err" ? "#991b1b" : "#854d0e" }}>{msg.text}</div>}

      {/* 25% strikes rail (of the selected expiry date) · 75% chart */}
      <div style={{ display: "flex", gap: 16, alignItems: "stretch", flexWrap: "wrap" }}>
        {/* LEFT — clickable strike list for the selected expiry */}
        <div style={card({ padding: 12, flex: "0 0 24%", minWidth: 180, maxHeight: 584, display: "flex", flexDirection: "column" })}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Strikes</div>
          <div style={{ fontSize: 11, color: C.sub, marginBottom: 10, fontFamily: "monospace" }}>{expiry || "— pick expiry —"}{strikes.length ? ` · ${strikes.length}` : ""}</div>
          <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 4, paddingRight: 4 }}>
            {!strikes.length && <div style={{ fontSize: 12, color: C.sub, padding: "8px 4px" }}>No strikes for this expiry.</div>}
            {strikes.map(s => {
              const active = String(s.strike) === String(strike);
              const avail = optType === "CE" ? s.ce : s.pe;
              return (
                <button key={s.strike} onClick={() => pickStrike(s.strike)} title={avail ? "" : `No stored ${optType} — will try Delta public API`}
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", borderRadius: 8, cursor: "pointer", textAlign: "left",
                    border: `1px solid ${active ? C.blue : C.border}`, background: active ? "#eff6ff" : "#fff", color: C.text, fontWeight: active ? 700 : 500, fontSize: 13, fontFamily: "monospace" }}>
                  <span>{s.strike.toLocaleString()}</span>
                  <span style={{ display: "flex", gap: 5 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: s.ce ? C.green : "#cbd5e1" }}>C</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: s.pe ? C.red : "#cbd5e1" }}>P</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* RIGHT — premium chart of the picked strike */}
        <div style={card({ padding: 16, flex: "1 1 0", minWidth: 320 })}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, fontSize: 13, color: C.text }}>
            <span style={{ fontWeight: 700, fontFamily: "monospace" }}>{data?.meta?.symbol || `${underlying} ${strike || "—"} ${optType}`} <span style={{ color: C.sub, fontWeight: 500 }}>· {data?.meta?.count || 0} candles</span></span>
            <span style={{ fontSize: 11, color: C.sub }}>option premium ($) · times {tz} · scroll = zoom · drag = pan</span>
          </div>
          <div style={{ position: "relative" }}>
            {legend && legend.open != null && (
              <div style={{ position: "absolute", top: 8, left: 10, zIndex: 3, display: "flex", gap: 12, fontSize: 12, background: "rgba(255,255,255,.9)", padding: "4px 10px", borderRadius: 6, border: `1px solid ${C.border}` }}>
                <span>O <b>{legend.open.toFixed(2)}</b></span>
                <span style={{ color: C.green }}>H <b>{legend.high.toFixed(2)}</b></span>
                <span style={{ color: C.red }}>L <b>{legend.low.toFixed(2)}</b></span>
                <span>C <b>{legend.close.toFixed(2)}</b></span>
              </div>
            )}
            <div ref={elRef} style={{ width: "100%", height: 520, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }} />
            {!cs.length && !loading && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: C.sub, fontSize: 14 }}>Pick a strike from the list.</div>}
          </div>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// VIX — ATM implied-volatility (Black-Scholes), a VIX-like gauge. Two views:
//   • Line   — day-wise ATM-IV area + straddle-% line (clean daily close). Reuses
//              /api/data-analysis/iv/:underlying.
//   • Candle — intraday ATM-IV bucketed to OHLC at a chosen interval (UTC-labelled).
//              /api/data-analysis/iv-candles/:underlying. Noisier (intraday quotes).
// Both need option data downloaded for the range.
const VIX_TFS = [
  { id: "day", label: "1D" }, { id: "4hour", label: "4h" }, { id: "60minute", label: "1h" },
  { id: "30minute", label: "30m" }, { id: "15minute", label: "15m" },
];
function VixChart() {
  const [underlying, setUnderlying] = useState("BTC");
  const [view, setView] = useState("line");          // 'line' | 'candle'
  const [cInterval, setCInterval] = useState("day");  // candle bucket
  // Line view keeps a long history; candle view compute is intraday, so default shorter.
  const [from, setFrom] = useState(shiftDays(isoDate(new Date()), -180));
  const [to, setTo] = useState(isoDate(new Date()));
  const [resp, setResp] = useState(null);   // line: /iv response
  const [cResp, setCResp] = useState(null);  // candle: /iv-candles response
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [legend, setLegend] = useState(null);

  const elRef = useRef(null), chartRef = useRef(null), ivRef = useRef(null), seriesRef = useRef([]);

  const load = useCallback(async (v, iv, f, t) => {
    const mode = v || view, tf = iv || cInterval, ff = f || from, tt = t || to;
    setLoading(true); setMsg(null);
    try {
      if (mode === "candle") {
        const res = await dataAnalysisAPI.ivCandles(underlying, { from: ff, to: tt, interval: tf });
        setCResp(res.data);
        if (!res.data.candles?.length) setMsg({ type: "warn", text: "No IV candles in this range." });
      } else {
        const res = await dataAnalysisAPI.iv(underlying, ff, tt);
        setResp(res.data);
        if (!res.data.days?.length) setMsg({ type: "warn", text: "No IV data in this range." });
      }
    } catch (e) { (mode === "candle" ? setCResp : setResp)(null); setMsg({ type: "err", text: e.response?.data?.message || e.message }); }
    finally { setLoading(false); }
  }, [underlying, view, cInterval, from, to]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  // Line data: API newest-first → ascending, IV in %, drop null-IV days.
  const rows = useMemo(() => (resp?.days || []).slice().sort((a, b) => a.date.localeCompare(b.date)).filter(d => d.iv != null), [resp]);
  const straddleRows = useMemo(() => (resp?.days || []).slice().sort((a, b) => a.date.localeCompare(b.date)).filter(d => d.straddlePct != null), [resp]);
  const candles = useMemo(() => (cResp?.candles || []).map(c => ({ ...c, _t: toTime(c) })), [cResp]);
  const hasData = view === "candle" ? candles.length : rows.length;

  useEffect(() => {
    const el = elRef.current; if (!el) return;
    const chart = createChart(el, {
      width: el.clientWidth, height: 460,
      layout: { background: { color: "#fff" }, textColor: "#334155", fontSize: 12 },
      grid: { vertLines: { color: "#eef2f7" }, horzLines: { color: "#eef2f7" } },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: C.border, scaleMargins: { top: 0.12, bottom: 0.1 } },
      timeScale: { borderColor: C.border, timeVisible: false, secondsVisible: false, rightOffset: 4 },
    });
    chartRef.current = chart;
    chart.subscribeCrosshairMove((p) => { if (!p.time || !p.seriesData || !ivRef.current) { setLegend(null); return; } const v = p.seriesData.get(ivRef.current); setLegend(v ? { time: p.time, val: v.value != null ? v.value : v.close } : null); });
    const ro = new ResizeObserver(() => chart.applyOptions({ width: el.clientWidth }));
    ro.observe(el);
    return () => { ro.disconnect(); chart.remove(); chartRef.current = null; };
  }, []);

  useEffect(() => {
    const chart = chartRef.current; if (!chart) return;
    seriesRef.current.forEach(s => chart.removeSeries(s)); seriesRef.current = []; ivRef.current = null;
    const add = (s) => { seriesRef.current.push(s); return s; };
    const pctFmt = { type: "custom", formatter: (v) => `${v.toFixed(1)}%` };

    if (view === "candle") {
      chart.applyOptions({ timeScale: { timeVisible: cInterval !== "day" } });
      if (!candles.length) return;
      const up = C.green, dn = C.red;
      const cd = add(chart.addCandlestickSeries({ upColor: up, downColor: dn, borderUpColor: up, borderDownColor: dn, wickUpColor: up, wickDownColor: dn, priceFormat: pctFmt }));
      cd.setData(candles.map(c => ({ time: c._t, open: c.o, high: c.h, low: c.l, close: c.c })));
      ivRef.current = cd;
    } else {
      chart.applyOptions({ timeScale: { timeVisible: false } });
      if (!rows.length) return;
      const iv = add(chart.addAreaSeries({ lineColor: C.amber, topColor: "rgba(245,158,11,.28)", bottomColor: "rgba(245,158,11,.02)", lineWidth: 2, priceLineVisible: false, priceFormat: pctFmt }));
      iv.setData(rows.map(d => ({ time: d.date, value: d.iv })));
      ivRef.current = iv;
      if (straddleRows.length) {
        const st = add(chart.addLineSeries({ color: "#94a3b8", lineWidth: 1, lineStyle: 2, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false }));
        st.setData(straddleRows.map(d => ({ time: d.date, value: d.straddlePct })));
      }
    }
    chart.timeScale().fitContent();
  }, [view, cInterval, rows, straddleRows, candles]);

  const s = view === "candle" ? cResp?.summary : resp?.summary;
  const stat = (label, val, color) => (
    <div style={{ padding: "10px 14px", borderRadius: 10, background: C.bg, border: `1px solid ${C.border}`, minWidth: 110 }}>
      <div style={{ fontSize: 11, color: C.sub, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: color || C.text }}>{val}</div>
    </div>
  );
  const fmtIv = (v) => v == null ? "—" : (typeof v === "object" ? `${v.value}%` : `${v}%`);

  return (
    <>
      <div style={card({ padding: 16, marginBottom: 16 })}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <label style={{ display: "block", fontSize: 12, color: C.sub, marginBottom: 6 }}>Underlying</label>
            <select value={underlying} onChange={e => setUnderlying(e.target.value)} style={sel()}>{OPT_UNDERLYINGS.map(u => <option key={u} value={u}>{u}</option>)}</select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: C.sub, marginBottom: 6 }}>View</label>
            <div style={{ display: "flex" }}>
              {["line", "candle"].map(v => <button key={v} onClick={() => { setView(v); if (v === "candle" && from < shiftDays(to, -60)) { const nf = shiftDays(to, -45); setFrom(nf); load(v, cInterval, nf, to); } else load(v, cInterval, from, to); }} style={{ ...chip(view === v), borderRadius: v === "line" ? "8px 0 0 8px" : "0 8px 8px 0", textTransform: "capitalize" }}>{v}</button>)}
            </div>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: C.sub, marginBottom: 6 }}>From</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={sel()} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: C.sub, marginBottom: 6 }}>To</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} style={sel()} />
          </div>
          <button onClick={() => load()} disabled={loading} style={{ ...chip(false), background: loading ? "#93c5fd" : C.blue, color: "#fff", border: "none" }}>{loading ? "Loading…" : "Load"}</button>
          <button onClick={() => chartRef.current?.timeScale().fitContent()} style={{ ...chip(false), marginLeft: "auto" }}>Fit</button>
        </div>
        {view === "candle" && (
          <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: C.sub, marginRight: 4 }}>Candle:</span>
            {VIX_TFS.map(t => <button key={t.id} onClick={() => { setCInterval(t.id); load("candle", t.id, from, to); }} style={chip(cInterval === t.id)}>{t.label}</button>)}
          </div>
        )}
        {s && (
          <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
            {stat("Avg IV", fmtIv(s.avgIv), C.amber)}
            {stat("Max IV", fmtIv(s.maxIv), C.red)}
            {stat("Min IV", fmtIv(s.minIv), C.green)}
            {view === "candle" ? stat("Bars", `${cResp?.meta?.count || 0}`) : stat("Avg straddle", s.avgStraddlePct != null ? `${s.avgStraddlePct}%` : "—")}
            {stat("Days", view === "candle" ? `${s.days || 0}` : (s.coverage ? `${s.coverage.withData}/${s.coverage.total}` : "—"))}
          </div>
        )}
      </div>

      {msg && <div style={{ marginBottom: 14, padding: "12px 16px", borderRadius: 10, fontSize: 14, background: msg.type === "err" ? "#fee2e2" : "#fef9c3", color: msg.type === "err" ? "#991b1b" : "#854d0e" }}>{msg.text}</div>}

      <div style={card({ padding: 16 })}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, fontSize: 13, color: C.text }}>
          <span style={{ fontWeight: 700 }}>{underlying} <span style={{ color: C.sub, fontWeight: 500 }}>ATM implied volatility (VIX-like){view === "candle" ? " · intraday OHLC (UTC)" : ""}</span></span>
          <span style={{ fontSize: 11, color: C.sub }}>
            {view === "candle"
              ? <>OHLC of ATM-IV · noisier than the line</>
              : <><span style={{ color: C.amber, fontWeight: 700 }}>▬ IV%</span>{"  "}<span style={{ color: "#94a3b8", fontWeight: 700 }}>┄ straddle % of spot</span></>}
          </span>
        </div>
        <div style={{ position: "relative" }}>
          {legend && legend.val != null && (
            <div style={{ position: "absolute", top: 8, left: 10, zIndex: 3, fontSize: 12, background: "rgba(255,255,255,.9)", padding: "4px 10px", borderRadius: 6, border: `1px solid ${C.border}` }}>
              <b>{legend.time}</b> · IV <b style={{ color: C.amber }}>{Number(legend.val).toFixed(1)}%</b>
            </div>
          )}
          <div ref={elRef} style={{ width: "100%", height: 460, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }} />
          {!hasData && !loading && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: C.sub, fontSize: 14, textAlign: "center", padding: 20 }}>No IV data. Download {underlying} option {view === "candle" ? "+ perp 1-min " : ""}data (Historical Data tab) for this range, then Load.</div>}
        </div>
      </div>
    </>
  );
}
